/**
 * Users — Invitations tab (W1a-05).
 *
 * Tenant-scoped invitations list with admin actions:
 *   - Resend     — rotates token + extends expiry by 7 days
 *   - Revoke     — flips status to revoked (link becomes dead). It does NOT
 *                  delete the invitee: their membership moves to
 *                  `invited_revoked` and they stay on the Users list with a
 *                  revoked-invitation badge (B2c-T1/T2).
 *   - Copy link  — surfaced ONLY right after a successful resend (the
 *                  raw token is internal-only otherwise; we never have
 *                  it from the list-fetch).
 *   - New User   — opens the W1a-03 CreateUserDialog
 *
 * B2c-T2 — ONE NAME FOR ONE STATE. The stored value was `cancelled` and
 * this tab's label was "Revoked"; c32 renamed the value (founder decision 15)
 * and the vocabulary is now `InvitationStatus` from `@argusai/shared`. The
 * tab, the pill, the toast and the store all say the same word.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import {
  Plus, Search, Send, Copy, X as XIcon, MoreHorizontal, Clock,
  Mail, Link2, AlertCircle, XCircle, CheckCircle2, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Tabs, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  useInvitations,
  useResendInvitation,
  useRevokeInvitation,
  type InvitationRow,
} from '@/lib/use-users';
import { useHasPermission } from '@/lib/use-has-permission';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import { UserAvatar } from '@/components/users/user-badges';
import { CreateUserDialog } from '@/components/users/create-user-dialog';
import { InviteLinkModal, type EmailDeliveryStatus } from '@/components/users/invite-link-modal';
// B2c-T2 — THE invitation status alphabet, from the one shared declaration.
import { type InvitationStatus } from '@argusai/shared/schemas/invitation.schema';

export const Route = createFileRoute('/users/invitations')({
  component: InvitationsTab,
});

/**
 * WHICH states this tab offers as a filter — a subset of the alphabet with
 * `all` added, so the values are the stored values (B2c-T2).
 */
type StatusFilter = Extract<
  InvitationStatus,
  'pending' | 'expired' | 'revoked' | 'accepted'
> | 'all';

/**
 * Rows per page on this tab (Q3 — the API caps a page at 100, default 20).
 */
const INVITATIONS_PAGE_SIZE = 50;

function InvitationsTab() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const canManage = useHasPermission('write', 'users');

  // USR-F1-T2 — THE SERVER ANSWERS (rails/users.md cause 1). The tab is the
  // `status` predicate, the search box is `q`, the pager is `page`, and the
  // badges are the response's `counts`. This page used to fetch one page of
  // 100 (oldest first) and filter, search and count it here — so any
  // invitation after the 100 oldest could be neither seen, resent nor revoked.
  const debouncedSearch = useDebouncedValue(search.trim(), 300);

  // A new search starts at page 1. Adjusted during render (not in an effect)
  // so the stale page is never requested with the new term.
  const [pagedSearch, setPagedSearch] = useState(debouncedSearch);
  if (pagedSearch !== debouncedSearch) {
    setPagedSearch(debouncedSearch);
    setPage(1);
  }

  const list = useInvitations({
    status: statusFilter === 'all' ? undefined : statusFilter,
    q: debouncedSearch || undefined,
    page,
    pageSize: INVITATIONS_PAGE_SIZE,
    enabled: canManage,
  });

  // A mutation (revoke, resend, …) can empty the last page: land on the last
  // page that still has rows rather than on an empty one. Adjusted during
  // render, as the search reset above, so React discards this render before it
  // commits — an effect would paint one frame of the empty state and a pager
  // reading "Showing 51–50 of 50" first (review USR-F1-T2-R2).
  const lastPage = Math.max(1, list.pagination?.totalPages ?? 1);
  if (list.pagination && page > lastPage) {
    setPage(lastPage);
  }

  // The badges are the server's counts. Hold the last answer while the next
  // bucket loads so they do not drop to zero between tabs.
  const lastCounts = useRef(list.counts);
  if (list.counts) lastCounts.current = list.counts;
  const counts = list.counts ?? lastCounts.current;

  const changeStatus = (next: StatusFilter) => {
    setStatusFilter(next);
    setPage(1);
  };

  const handleCreated = (userId: string) => {
    setCreateOpen(false);
    toast.success('Invitation sent', {
      description: 'User will receive an email to complete setup.',
    });
    // Refresh list so the new pending invitation appears.
    list.refetch();
    navigate({ to: '/users/$userId' as never, params: { userId } as never });
  };

  if (!canManage) {
    return (
      <div className="flex flex-col h-full p-6 space-y-4">
        <div>
          <h1 className="text-xl font-semibold">Invitations</h1>
          <p className="text-xs text-muted-foreground mt-1">
            Pending user onboarding. Invitations expire 7 days after creation.
          </p>
        </div>
        <div className="rounded-lg border bg-card shadow-sm py-16 text-center">
          <AlertCircle className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-sm font-medium">Invitation management requires user management permission</p>
          <p className="text-xs text-muted-foreground mt-1">
            Ask a tenant administrator to manage pending invites.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full p-6 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Invitations</h1>
          <p className="text-xs text-muted-foreground mt-1">
            Pending user onboarding. Invitations expire 7 days after creation.
          </p>
        </div>
        <div className="flex gap-2">
          {canManage && (
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="h-3.5 w-3.5 mr-1.5" /> New Invitation
            </Button>
          )}
        </div>
      </div>

      {/* Status tabs */}
      <Tabs value={statusFilter} onValueChange={(v) => changeStatus(v as StatusFilter)}>
        <TabsList className="h-9">
          <TabsTrigger value="pending" className="text-xs gap-1.5">
            Pending <Badge variant="secondary" className="text-[9px] h-4 px-1 tabular-nums">{counts?.pending ?? 0}</Badge>
          </TabsTrigger>
          <TabsTrigger value="accepted" className="text-xs gap-1.5">
            Accepted <Badge variant="secondary" className="text-[9px] h-4 px-1 tabular-nums">{counts?.accepted ?? 0}</Badge>
          </TabsTrigger>
          <TabsTrigger value="expired" className="text-xs gap-1.5">
            Expired <Badge variant="secondary" className="text-[9px] h-4 px-1 tabular-nums">{counts?.expired ?? 0}</Badge>
          </TabsTrigger>
          {/* B2c-T2 — the tab's VALUE is now the stored value. It read
              `cancelled` under a "Revoked" label: the exact two-names shape. */}
          <TabsTrigger value="revoked" className="text-xs gap-1.5">
            Revoked <Badge variant="secondary" className="text-[9px] h-4 px-1 tabular-nums">{counts?.revoked ?? 0}</Badge>
          </TabsTrigger>
          <TabsTrigger value="all" className="text-xs gap-1.5">
            All <Badge variant="secondary" className="text-[9px] h-4 px-1 tabular-nums">{counts?.all ?? 0}</Badge>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <Card>
        <CardContent className="p-3">
          <div className="relative max-w-md">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search email or inviter…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 pl-8 text-xs"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <div className="rounded-lg border bg-card shadow-sm flex-1 overflow-auto">
        <table className="w-full text-xs border-collapse" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: 'auto', minWidth: 260 }} />
            <col style={{ width: 110 }} />
            <col style={{ width: 200 }} />
            <col style={{ width: 110 }} />
            <col style={{ width: 110 }} />
            <col style={{ width: 86 }} />
          </colgroup>
          <thead className="bg-muted/50 sticky top-0 z-10">
            <tr className="border-b">
              <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Invitee</th>
              <th className="px-2 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Status</th>
              <th className="px-2 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Invited by</th>
              <th className="px-2 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Role</th>
              <th className="px-2 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Expires</th>
              <th className="px-2 py-2 text-right text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading ? (
              <tr>
                <td colSpan={6}>
                  <div className="py-16 text-center text-xs text-muted-foreground">
                    Loading invitations…
                  </div>
                </td>
              </tr>
            ) : list.error ? (
              <tr>
                <td colSpan={6}>
                  <div className="py-16 text-center">
                    <AlertCircle className="h-10 w-10 mx-auto text-destructive/40 mb-3" />
                    <p className="text-sm font-medium text-destructive">Could not load invitations</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {String((list.error as Error)?.message ?? 'Try again later.')}
                    </p>
                  </div>
                </td>
              </tr>
            ) : list.invitations.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <div className="py-16 text-center">
                    <Mail className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
                    {/* Closes #133 — distinguish "no invitations in
                        this status" from "search excluded all". */}
                    {debouncedSearch !== '' ? (
                      <>
                        <p className="text-sm font-medium">No invitations match your search</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Try a different search term or clear it to see all
                          {statusFilter !== 'all' ? ` ${statusFilter}` : ''} invitations.
                        </p>
                        <Button size="sm" variant="outline" className="mt-3" onClick={() => setSearch('')}>
                          Clear search
                        </Button>
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-medium">
                          No {statusFilter === 'all' ? '' : statusFilter} invitations
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {statusFilter === 'pending'
                            ? 'Click "New Invitation" to invite your first user.'
                            : `No ${statusFilter} invitations to show.`}
                        </p>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              list.invitations.map((inv) => (
                <InvitationRowComponent
                  key={inv.id}
                  invitation={inv}
                  canManage={canManage}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* USR-F1-T2 — the pager walks the server's pages of this bucket. */}
      {list.pagination && list.pagination.totalCount > 0 && (
        <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
          <span className="tabular-nums">
            Showing {(list.pagination.page - 1) * list.pagination.pageSize + 1}–
            {Math.min(list.pagination.page * list.pagination.pageSize, list.pagination.totalCount)} of{' '}
            {list.pagination.totalCount}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft className="h-3.5 w-3.5 mr-1" />Previous
            </Button>
            <span className="px-2 tabular-nums">
              Page {list.pagination.page} of {Math.max(1, list.pagination.totalPages)}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              disabled={page >= list.pagination.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next<ChevronRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
        </div>
      )}

      <CreateUserDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={handleCreated}
      />
    </div>
  );
}

function InvitationRowComponent({
  invitation,
  canManage,
}: {
  invitation: InvitationRow;
  canManage: boolean;
}) {
  const inviterName = `${invitation.invitedBy.firstName ?? ''} ${
    invitation.invitedBy.lastName ?? ''
  }`.trim() || invitation.invitedBy.email;

  const inviterFull = invitation.invitedBy.firstName || invitation.invitedBy.lastName
    ? inviterName
    : invitation.invitedBy.email;

  const expiresIn = Math.ceil(
    (new Date(invitation.expiresAt).getTime() - Date.now()) / 86_400_000,
  );
  const isExpiring = invitation.status === 'pending' && expiresIn <= 2;

  // Review B2cT2-BF-01 — the row's "re-invite" item needs somewhere to go.
  const navigate = useNavigate();
  const resend = useResendInvitation();
  const revoke = useRevokeInvitation();
  // Closes #219: Revoke kills a live link and shifts the counts, so a
  // confirmation gates it behind a real affirmative click.
  //
  // B2c-T2 — it is NOT a delete any more. The comment here said "the user row
  // disappears from /users immediately", which is what the handler used to do:
  // it soft-deleted the placeholder person on the GLOBAL users row, removing
  // them from every tenant. The person now stays on the roster in
  // `invited_revoked`, and removing them is a separate, `delete:users` action.
  const [confirmRevokeOpen, setConfirmRevokeOpen] = useState(false);
  // W1a-08 invite-recovery — persistent modal replaces the lossy
  // post-Resend toast. The toast variant auto-dismissed after ~12s
  // and the raw URL could not be re-retrieved (only the hash is
  // stored). The modal stays open until the admin closes it
  // explicitly and lets them copy the URL repeatedly.
  const [inviteLinkModal, setInviteLinkModal] = useState<{
    inviteUrl: string;
    expiresAt: string;
    emailDelivery: EmailDeliveryStatus;
    emailError?: string;
  } | null>(null);

  /** Normalise the API's URL (may be absolute or path-only) to a
   *  full origin-prefixed string the admin can copy. */
  const toAbsoluteUrl = (raw: string): string =>
    raw.startsWith('http')
      ? raw
      : `${window.location.origin}${raw.startsWith('/') ? '' : '/'}${raw}`;

  const statusStyles: Record<
    InvitationRow['status'],
    { icon: typeof Clock; color: string; label: string }
  > = {
    pending: {
      icon: Clock,
      color: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30',
      label: 'Pending',
    },
    accepted: {
      icon: CheckCircle2,
      color: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
      label: 'Accepted',
    },
    declined: {
      icon: XCircle,
      color: 'bg-zinc-500/10 text-zinc-700 dark:text-zinc-300 border-zinc-500/30',
      label: 'Declined',
    },
    expired: {
      icon: AlertCircle,
      color: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30',
      label: 'Expired',
    },
    revoked: {
      icon: XCircle,
      color: 'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30',
      label: 'Revoked',
    },
  };
  const s = statusStyles[invitation.status];
  const StatusIcon = s.icon;

  const isPending = invitation.status === 'pending';

  /**
   * Resend the invitation and surface the fresh URL via the persistent
   * InviteLinkModal. The same code path is used by both the "Resend"
   * row icon and the new "Get Link" dropdown action — the only
   * difference is the dropdown action exists specifically so admins
   * can re-mint a URL even when SMTP IS working (they need the link
   * for ticket systems, side-channel delivery, etc.).
   */
  const handleResend = async () => {
    try {
      const res = await resend.mutateAsync({ id: invitation.id });
      setInviteLinkModal({
        inviteUrl: toAbsoluteUrl(res.inviteUrl),
        expiresAt: res.expiresAt,
        // Older deployments without the W1a-08 field on the response
        // are treated as 'disabled' so the banner is honest about
        // the (unknown) email-delivery state.
        emailDelivery: res.emailDelivery ?? 'disabled',
        emailError: res.emailError,
      });
    } catch (err) {
      const message =
        (err as { error?: { message?: string }; message?: string })?.error?.message ??
        (err as Error)?.message ??
        'Could not resend invitation';
      toast.error(message);
    }
  };

  const handleRevoke = async () => {
    try {
      await revoke.mutateAsync({ id: invitation.id });
      toast.success('Invitation revoked', {
        description: `${invitation.email} can no longer accept this invitation.`,
      });
      setConfirmRevokeOpen(false);
    } catch (err) {
      const message =
        (err as { error?: { message?: string }; message?: string })?.error?.message ??
        (err as Error)?.message ??
        'Could not revoke invitation';
      toast.error(message);
    }
  };

  return (
    <>
    <tr className="border-b text-xs hover:bg-muted/30">
      <td className="px-4 py-3 align-middle">
        <div className="flex items-center gap-2 min-w-0">
          <UserAvatar name={invitation.email} color="blue" size="sm" />
          <div className="min-w-0 flex-1">
            <div className="font-medium truncate">{invitation.email}</div>
            <div className="text-[10px] text-muted-foreground truncate">
              Created {new Date(invitation.createdAt).toLocaleDateString()}
            </div>
          </div>
        </div>
      </td>

      <td className="px-2 py-3 align-middle">
        <Badge variant="outline" className={cn('text-[10px] gap-1 w-fit', s.color)}>
          <StatusIcon className="h-2.5 w-2.5" />
          {s.label}
        </Badge>
      </td>

      <td className="px-2 py-3 align-middle">
        <div className="min-w-0">
          <div className="truncate" title={invitation.invitedBy.email}>{inviterFull}</div>
          <div className="text-[10px] text-muted-foreground truncate">
            {invitation.invitedBy.email}
          </div>
        </div>
      </td>

      <td className="px-2 py-3 align-middle">
        <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-5">
          {invitation.role}
        </Badge>
      </td>

      <td className="px-2 py-3 align-middle">
        {isPending ? (
          <span className={cn(
            'text-[11px] tabular-nums',
            isExpiring && 'text-amber-600 dark:text-amber-400 font-semibold',
          )}>
            {expiresIn <= 0 ? 'Expiring today' : `in ${expiresIn}d`}
          </span>
        ) : (
          <span className="text-[11px] tabular-nums text-muted-foreground">
            {new Date(invitation.expiresAt).toLocaleDateString()}
          </span>
        )}
      </td>

      <td className="px-2 py-3 align-middle">
        <div className="flex items-center justify-end gap-0">
          {canManage && isPending && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              title="Resend invitation"
              onClick={handleResend}
              disabled={resend.isPending}
            >
              <Send className="h-3.5 w-3.5" />
            </Button>
          )}
          {canManage && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="More">
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                {isPending && (
                  <>
                    <DropdownMenuItem
                      className="text-xs"
                      onSelect={handleResend}
                      disabled={resend.isPending}
                    >
                      <Send className="h-3.5 w-3.5 mr-2" /> Resend (new link)
                    </DropdownMenuItem>
                    {/* W1a-08 — Always-available "Get Link" action.    */}
                    {/* Same backend call as Resend (rotates token +     */}
                    {/* mints fresh URL). Lets admins recover a stalled */}
                    {/* invitation even when SMTP works but the email   */}
                    {/* was missed / filtered / forwarded incorrectly,  */}
                    {/* and gives them a copyable URL for ticket / chat */}
                    {/* side-channel delivery.                          */}
                    <DropdownMenuItem
                      className="text-xs"
                      onSelect={handleResend}
                      disabled={resend.isPending}
                    >
                      <Link2 className="h-3.5 w-3.5 mr-2" /> Get invite link
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-xs"
                      onSelect={() => {
                        navigator.clipboard.writeText(invitation.email);
                        toast.success('Email copied');
                      }}
                    >
                      <Copy className="h-3.5 w-3.5 mr-2" /> Copy email
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-xs text-red-600 dark:text-red-400"
                      onSelect={(e) => {
                        // Don't fire revoke immediately — open the
                        // confirmation dialog instead (closes #219).
                        e.preventDefault();
                        setConfirmRevokeOpen(true);
                      }}
                      disabled={revoke.isPending}
                    >
                      <XIcon className="h-3.5 w-3.5 mr-2" /> Revoke invitation
                    </DropdownMenuItem>
                  </>
                )}
                {!isPending && (
                  <>
                    <DropdownMenuItem
                      className="text-xs"
                      onSelect={() => {
                        navigator.clipboard.writeText(invitation.email);
                        toast.success('Email copied');
                      }}
                    >
                      <Copy className="h-3.5 w-3.5 mr-2" /> Copy email
                    </DropdownMenuItem>
                    {/*
                      Review B2cT2-BF-01 — this was a DISABLED item reading
                      "Use New User to re-invite", and that instruction was
                      wrong as well as dead: since B2c-T1 the revoked invitee
                      stays on the roster, so New User answers 409 for their
                      address and tells the operator to re-invite them instead.
                      The way back is the table's `invited_revoked → invited`
                      edge, walked by `POST /users/:id/reinvite` from the
                      person's own page — which is where this now goes. The
                      invitation row carries an email, not a user id, so it
                      opens the roster filtered to that address rather than
                      inventing a lookup this list does not have.
                    */}
                    <DropdownMenuItem
                      className="text-xs"
                      onSelect={() => {
                        navigate({
                          to: '/users' as never,
                          search: { q: invitation.email } as never,
                        });
                      }}
                    >
                      <Link2 className="h-3.5 w-3.5 mr-2" /> Find on Users to re-invite
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </td>
    </tr>
    {/* Closes #219: Revoke confirmation. AlertDialog content is
        portalled to <body>, so rendering it inside the row component
        (rather than the parent table) is safe and keeps the row's
        revoke handler colocated with its state. */}
    <AlertDialog open={confirmRevokeOpen} onOpenChange={setConfirmRevokeOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Revoke invitation for {invitation.email}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            This invitation link becomes dead immediately and the invited
            user can no longer accept it. They <strong>stay</strong> on the
            Users list, marked as a revoked invitation, and can be invited
            again. Pending count drops by one and revoked count goes up by
            one. The action is recorded in the audit trail.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={revoke.isPending}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              // AlertDialogAction auto-closes the dialog on click; we
              // need to keep the dialog open while the mutation runs
              // and close it from `handleRevoke` only on success.
              e.preventDefault();
              void handleRevoke();
            }}
            disabled={revoke.isPending}
            className="bg-red-600 hover:bg-red-700 text-white focus-visible:ring-red-600"
          >
            {revoke.isPending ? 'Revoking…' : 'Revoke invitation'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* W1a-08 invite-recovery — persistent modal that replaces the */}
    {/* lossy post-Resend toast. The toast variant auto-dismissed   */}
    {/* after ~12s and the raw URL could not be re-retrieved        */}
    {/* afterwards (the row only stores the hash). */}
    {inviteLinkModal && (
      <InviteLinkModal
        open={inviteLinkModal !== null}
        onOpenChange={(open) => {
          if (!open) setInviteLinkModal(null);
        }}
        inviteUrl={inviteLinkModal.inviteUrl}
        expiresAt={inviteLinkModal.expiresAt}
        emailDelivery={inviteLinkModal.emailDelivery}
        emailError={inviteLinkModal.emailError}
        recipientEmail={invitation.email}
        onResend={handleResend}
      />
    )}
    </>
  );
}
